/**
 * Focus-mode toggle for the artifact canvas header (#1378).
 *
 * Enters/exits full-screen "focus mode", which hides the left platform sidebar
 * so the chat panel and the canvas fill the whole screen. Shared across every
 * canvas kind and placed just left of the Download action. Desktop-only: on
 * small screens the canvas is already full-screen and the sidebar is an
 * off-canvas sheet, so the toggle would be meaningless.
 *
 * @module components/artifacts/focus-mode-toggle
 */

import { Maximize2, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useArtifactStore } from "@/store/artifact-store";

/**
 * Icon button bound to `useArtifactStore().focusMode`. The icon + labels flip
 * between enter (Maximize2) and exit (Minimize2) so the current state is clear.
 */
export function FocusModeToggle() {
  const focusMode = useArtifactStore((s) => s.focusMode);
  const toggleFocusMode = useArtifactStore((s) => s.toggleFocusMode);

  const label = focusMode ? "Exit full screen" : "Full screen";

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      // Hidden below lg: on mobile/tablet the canvas is already full-screen and
      // the sidebar is an off-canvas sheet, so there's nothing to focus.
      className="hidden lg:inline-flex"
      onClick={toggleFocusMode}
      title={label}
      aria-label={label}
      aria-pressed={focusMode}
      data-testid="artifact-focus-toggle"
    >
      {focusMode ? (
        <Minimize2 className="size-5" />
      ) : (
        <Maximize2 className="size-5" />
      )}
    </Button>
  );
}
